import React, { useMemo, useCallback } from 'react';
import styled from 'styled-components';

import {
  U21Select,
  U21Spacer,
  U21Typography,
} from 'app/shared/u21-ui/components';
import pluralize from 'pluralize';

enum TimeElements {
  YEAR,
  MONTH,
  DAY,
}

const TIME_UNITS_CONVERSIONS: {
  [time in TimeElements]: {
    conversionToMS: number;
    label: string;
    options: { text: string; value: number }[];
  };
} = {
  [TimeElements.YEAR]: {
    conversionToMS: 365 * 24 * 60 * 60 * 1000,
    label: 'Years',
    options: Array(20)
      .fill(null)
      .map((_, i) => ({ text: `${i}`, value: i })),
  },
  [TimeElements.MONTH]: {
    conversionToMS: 30 * 24 * 60 * 60 * 1000,
    label: 'Months',
    options: Array(11)
      .fill(null)
      .map((_, i) => ({ text: `${i}`, value: i })),
  },
  [TimeElements.DAY]: {
    conversionToMS: 24 * 60 * 60 * 1000,
    label: 'Days',
    options: Array(29)
      .fill(null)
      .map((_, i) => ({ text: `${i}`, value: i })),
  },
};

interface Props {
  value: number;
  onChange: (value: number) => void;
  viewOnly?: boolean;
}

export const TimeDeltaInput = React.memo((props: Props) => {
  const { value, onChange, viewOnly } = props;
  const localValues = useMemo(() => {
    let total = value;
    const newValues = { year: 0, month: 0, day: 0 };
    Object.keys(TIME_UNITS_CONVERSIONS).forEach((key) => {
      const count = Math.floor(
        total / TIME_UNITS_CONVERSIONS[key].conversionToMS,
      );
      total -= TIME_UNITS_CONVERSIONS[key].conversionToMS * count;
      newValues[key] = count;
    });
    return newValues;
  }, [value]);

  const updateTotal = useCallback(
    (newState: typeof localValues) => {
      let total = 0;
      Object.keys(TIME_UNITS_CONVERSIONS).forEach((key) => {
        total += newState[key] * TIME_UNITS_CONVERSIONS[key].conversionToMS;
      });
      onChange(total);
    },
    [onChange],
  );

  if (viewOnly) {
    return (
      <U21Typography variant="body2">
        {new Intl.ListFormat('en-US').format(
          Object.entries(TIME_UNITS_CONVERSIONS).map(([k, v]) =>
            pluralize(v.label, localValues[k], true),
          ),
        )}
      </U21Typography>
    );
  }

  return (
    <>
      {Object.keys(TIME_UNITS_CONVERSIONS).map((key) => {
        const config: ValueOf<typeof TIME_UNITS_CONVERSIONS> =
          TIME_UNITS_CONVERSIONS[key];

        return (
          <U21Spacer key={config.label} horizontal>
            <StyledU21Typography variant="body2">
              {config.label}
            </StyledU21Typography>
            <U21Select<number, false>
              clearable={false}
              value={localValues[key]}
              onChange={(newValue) => {
                if (newValue !== undefined) {
                  updateTotal({ ...localValues, [key]: newValue });
                }
              }}
              options={config.options}
            />
          </U21Spacer>
        );
      })}
    </>
  );
});

const StyledU21Typography = styled(U21Typography)`
  width: 80px;
`;
